import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "@/lib/siteContent";

export default function Terms() {
  return (
    <main data-testid="terms-page" className="bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 sm:py-32">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#0F2A44] hover:text-[#D4AF7F] transition-colors mb-12"
          data-testid="terms-back"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          Back to home
        </Link>
        <p className="text-xs uppercase tracking-[0.25em] text-[#6B7280] mb-6">
          Legal
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#0F2A44] mb-4 leading-tight">
          Terms of Service
        </h1>
        <p className="text-sm text-[#6B7280] mb-12">
          Effective Date: June 2, 2026
        </p>

        <div className="space-y-6 text-[#1A1A1A]/85 leading-relaxed">
          <p>
            These Terms of Service ("Terms") govern your access to and use of{" "}
            {COMPANY.domain} (the "Site"), operated by {COMPANY.legalName}{" "}
            ("Crownpath," "we," "us," or "our"). By accessing or using the Site,
            you agree to be bound by these Terms. If you do not agree, do not use
            the Site.
          </p>

          <Section title="1. Description of Service">
            <p>
              The Site is an informational website that provides information
              about freight dispatching services offered by Crownpath. The Site
              itself does not provide dispatch services. Dispatch services are
              governed by a separate written agreement between Crownpath and each
              individual carrier client.
            </p>
            <p>
              Nothing on the Site constitutes an offer to provide dispatch
              services, and no service relationship is created by using the Site
              or by submitting the contact form.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>
              You must be at least 18 years old and capable of entering into a
              binding contract to use the Site.
            </p>
          </Section>

          <Section title="3. Acceptable Use">
            <p>You agree not to:</p>
            <Bullets
              items={[
                "Use the Site for any unlawful purpose",
                "Attempt to gain unauthorized access to the Site or its systems",
                "Interfere with or disrupt the Site's operation",
                "Submit false, misleading, or fraudulent information",
                "Scrape, harvest, or extract data from the Site without permission",
                "Use the Site to transmit malware, viruses, or harmful code",
                "Impersonate any person or entity",
                "Engage in any activity that could damage the reputation of Crownpath or its clients",
              ]}
            />
          </Section>

          <Section title="4. Intellectual Property">
            <p>
              All content on the Site — including text, graphics, logos, images,
              and design — is the property of {COMPANY.legalName} or its
              licensors and is protected by copyright, trademark, and other
              intellectual property laws. You may not reproduce, distribute,
              modify, or create derivative works from any Site content without
              our prior written permission.
            </p>
          </Section>

          <Section title="5. Contact Form Submissions">
            <p>
              By submitting information through our contact form or otherwise
              communicating with us through the Site, you represent that the
              information you provide is accurate and that you are authorized to
              provide it. We may respond to your inquiry by phone, email, or
              other means using the contact details you provide.
            </p>
            <p>
              Submitting the contact form does not create a contract between you
              and Crownpath. Any dispatch service relationship requires a
              separately executed written agreement.
            </p>
          </Section>

          <Section title="6. Third-Party Links and Services">
            <p>
              The Site may contain links to third-party websites or services that
              are not owned or controlled by Crownpath. We are not responsible
              for the content, practices, or policies of any third-party sites.
              Your use of such sites is at your own risk.
            </p>
          </Section>

          <Section title="7. Disclaimers">
            <p className="uppercase tracking-wide text-sm">
              The Site is provided "as is" and "as available" without warranties
              of any kind, whether express or implied, including but not limited
              to implied warranties of merchantability, fitness for a particular
              purpose, and non-infringement.
            </p>
            <p>
              We do not warrant that the Site will be uninterrupted, error-free,
              secure, or free of viruses or other harmful components. Information
              on the Site is provided for general informational purposes only and
              should not be relied upon as legal, financial, or professional
              advice.
            </p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p className="uppercase tracking-wide text-sm">
              To the fullest extent permitted by law, {COMPANY.legalName}, its
              officers, employees, and agents shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages,
              including lost profits, lost data, or business interruption,
              arising out of or related to your use of the Site, even if we have
              been advised of the possibility of such damages.
            </p>
            <p className="uppercase tracking-wide text-sm">
              Our total liability arising out of or related to these Terms or the
              Site shall not exceed one hundred U.S. dollars ($100.00).
            </p>
          </Section>

          <Section title="9. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless {COMPANY.legalName}{" "}
              and its officers, employees, and agents from any claims, damages,
              liabilities, costs, and expenses (including reasonable attorneys'
              fees) arising out of or related to your use of the Site, your
              violation of these Terms, or your violation of any rights of a
              third party.
            </p>
          </Section>

          <Section title="10. Governing Law">
            <p>
              These Terms are governed by the laws of the State of Georgia,
              without regard to its conflict of laws principles. Any dispute
              arising out of or related to these Terms or the Site shall be
              resolved exclusively in the state or federal courts located in
              Fulton County, Georgia, and you consent to the jurisdiction of
              those courts.
            </p>
          </Section>

          <Section title="11. Severability">
            <p>
              If any provision of these Terms is found to be invalid or
              unenforceable, the remaining provisions shall remain in full force
              and effect.
            </p>
          </Section>

          <Section title="12. Entire Agreement">
            <p>
              These Terms, together with our Privacy Policy, constitute the
              entire agreement between you and Crownpath regarding your use of
              the Site.
            </p>
          </Section>

          <Section title="13. Changes to These Terms">
            <p>
              We may revise these Terms from time to time. When we do, we will
              update the "Effective Date" at the top of this page. Continued use
              of the Site after changes constitutes acceptance of the revised
              Terms.
            </p>
          </Section>

          <Section title="14. Contact Us">
            <p>If you have questions about these Terms, contact us at:</p>
            <address className="not-italic mt-4 space-y-1">
              <div className="font-medium text-[#0F2A44]">{COMPANY.legalName}</div>
              <div>
                Email:{" "}
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="text-[#0F2A44] border-b-2 border-[#D4AF7F] hover:text-[#D4AF7F] transition-colors"
                >
                  {COMPANY.email}
                </a>
              </div>
              <div>
                Phone:{" "}
                <a
                  href={COMPANY.phoneHref}
                  className="text-[#0F2A44] hover:text-[#D4AF7F] transition-colors"
                >
                  {COMPANY.phone}
                </a>
              </div>
            </address>
          </Section>
        </div>
      </div>
    </main>
  );
}

const Section = ({ title, children }) => (
  <section className="pt-8 space-y-4">
    <h2 className="font-serif text-2xl text-[#0F2A44]">{title}</h2>
    {children}
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2">
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </ul>
);
